import { NavLink, useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BrandLogo } from '@/components/brand/BrandLogo'
import { WorkspaceSwitcher } from '@/features/organization/components/WorkspaceSwitcher'
import { useAuthStore, useOrgStore } from '@/lib/store'
import { useCopilotAccess } from '@/features/copilot/hooks'
import { useCategoryTreeAccess } from '@/features/categories/hooks'
import { dashboardNavItems } from './nav-config'

interface AppSidebarProps {
  isMobile?: boolean
  onNavigate?: () => void
  isCollapsed?: boolean
  onCollapse?: () => void
  onToggleCollapsed?: () => void
  organizationIdOverride?: string | null
}

export function AppSidebar({ 
  isMobile, 
  onNavigate,
  isCollapsed = false,
  onCollapse,
  onToggleCollapsed,
  organizationIdOverride,
}: AppSidebarProps) {
  const { orgId } = useParams()
  const { user } = useAuthStore()
  const activeOrgId = useOrgStore((state) => state.activeOrgId)
  const activeOrgRole = useOrgStore((state) => state.activeOrgRole)
  const navigationOrgId = organizationIdOverride || activeOrgId || orgId
  const baseUrl = navigationOrgId ? `/o/${navigationOrgId}` : ''
  const isOrgAdmin = activeOrgRole === 'OWNER' || activeOrgRole === 'ADMIN'

  // Copilot is hidden by default; the capability endpoint tells us whether to show it.
  const copilotAccess = useCopilotAccess()
  const copilotEnabled = copilotAccess.data?.enabled === true

  // Categories («Категорії») is likewise hidden until its capability endpoint opts the user in.
  const categoryTreeAccess = useCategoryTreeAccess()
  const categoryTreeEnabled = categoryTreeAccess.data?.enabled === true

  const visibleNavItems = dashboardNavItems.filter((item) => {
    if (item.requireStaff && !user?.is_staff) {
      return false
    }
    if (item.requireKmpAdmin && !user?.is_kmp_admin) {
      return false
    }
    if (item.requireOrgAdmin && !isOrgAdmin) {
      return false
    }
    if (item.requireCopilot && !copilotEnabled) {
      return false
    }
    if (item.requireCategoryTree && !categoryTreeEnabled) {
      return false
    }
    return true
  })
  const primaryNavItems = visibleNavItems.filter((item) => item.placement !== 'secondary')
  const secondaryNavItems = visibleNavItems.filter((item) => item.placement === 'secondary')

  function renderNavItem(item: (typeof visibleNavItems)[number]) {
    const Icon = item.icon
    const content = (
      <>
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className={cn('min-w-0 flex-1 truncate transition-all', isCollapsed && !isMobile && 'hidden')}>
          {item.label}
        </span>
        {item.badge && !isCollapsed && !isMobile ? (
          <span className="rounded-full border border-[var(--mph-border)] bg-[var(--mph-bg-control)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--mph-text-tertiary)]">
            {item.badge}
          </span>
        ) : null}
      </>
    )

    return (
      <NavLink
        key={item.path}
        to={`${baseUrl}/${item.path}`}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-[9px] border border-transparent px-3 py-2 text-sm text-[var(--mph-text-secondary)] outline-none transition hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-text-primary)] focus-visible:shadow-[var(--mph-focus-ring)]',
            isActive && 'border-[var(--mph-accent-border)] bg-[var(--mph-accent-soft)] text-[var(--mph-accent)] shadow-[inset_0_0_0_1px_rgba(13,148,136,0.08)]',
            isCollapsed && !isMobile && 'justify-center px-2'
          )
        }
        onClick={onNavigate}
        title={item.label}
      >
        {content}
      </NavLink>
    )
  }

  return (
    <aside
      className={cn(
        'flex h-full w-full flex-col overflow-hidden border-r border-[var(--mph-border-subtle)] bg-[var(--mph-bg-chrome)] text-[var(--mph-text-primary)] transition-all duration-300',
        isMobile && 'h-auto border-r-0 border-b border-[var(--mph-border)]',
      )}
    >
      <div
        className={cn(
          'flex items-center border-b border-[var(--mph-border-subtle)] py-3',
          isCollapsed && !isMobile
            ? 'justify-center px-1.5'
            : isMobile
              ? 'justify-between px-4'
              : 'justify-start gap-2 px-2',
        )}
      >
        {isCollapsed && !isMobile ? (
          <button
            type="button"
            title="Розгорнути меню"
            aria-label="Розгорнути меню"
            onClick={onToggleCollapsed}
            className="inline-flex h-10 w-full items-center justify-center gap-0.5 rounded-[10px] text-[var(--mph-text-secondary)] transition hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-accent)] focus-visible:shadow-[var(--mph-focus-ring)]"
          >
            <BrandLogo
              variant="mark"
              showText={false}
              className="w-7 shrink-0 overflow-hidden"
              markClassName="h-7 w-7"
              textClassName="text-xs transition-all text-[var(--mph-text-primary)]"
            />
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          </button>
        ) : (
          <>
            <BrandLogo
              variant="mark"
              showText={false}
              className="w-7 shrink-0 overflow-hidden"
              markClassName="h-7 w-7"
              textClassName="text-xs transition-all text-[var(--mph-text-primary)]"
            />
            {activeOrgId || isMobile ? (
              <WorkspaceSwitcher placement="sidebar" compact className="min-w-0 flex-1" />
            ) : null}
            {!isMobile ? (
              <button
                type="button"
                title="Згорнути меню"
                aria-label="Згорнути меню"
                onClick={onCollapse}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[var(--mph-text-tertiary)] transition hover:bg-[var(--mph-bg-control)] hover:text-[var(--mph-accent)] focus-visible:shadow-[var(--mph-focus-ring)]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        <nav className={cn('space-y-1 px-2 py-3', isCollapsed && !isMobile && 'px-1.5')}>
          {primaryNavItems.map(renderNavItem)}
        </nav>
        <div className={cn('mt-auto space-y-2 px-2 pb-3', isCollapsed && !isMobile && 'px-1.5')}>
          {secondaryNavItems.length > 0 ? (
            <nav className="space-y-1 border-t border-[var(--mph-border-subtle)] pt-3">
              {secondaryNavItems.map(renderNavItem)}
            </nav>
          ) : null}
        </div>
      </div>
    </aside>
  )
}
